import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Home } from 'lucide-react';
import { DevicesPanel } from './Devices';
import { useQueryClient } from '@tanstack/react-query';
import type {
  CrucibleConfig,
  FurnaceStatus,
  TokenStatus,
  TokenVerifyDetail,
  TokenVerifyResult,
  RegistryTokenTestKind,
  RegistryTokenTestResult,
} from '@volley/crucible-ipc-types/ipc-channels';
import { useIpcQuery } from '../hooks/useIpc';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { FoundryShellHeader } from '../components/FoundryShellHeader';
import { useAppFrame } from '../AppFrameContext';
import { unflattenIpcError, type SerialisedError } from '@volley/crucible-ipc-types/error-types';
import { CrucibleErrorState } from '../components/CrucibleErrorState';
import { PrereqStatusList } from '../components/PrereqStatusList';
import { BundlePreview } from '../components/Diagnostics/BundlePreview';
import { SecretFlagBanner } from '../components/Diagnostics/SecretFlagBanner';
import {
  scanForSecrets,
  type SecretFlag,
} from '@volley/crucible-ipc-types/diagnostics-secret-scan';
import { normalisePreviewPhoneCapOverride } from '../workspace/preview-controller-policy';

type SettingsTab =
  | 'connections'
  | 'preferences'
  | 'privacy'
  | 'advanced'
  | 'diagnostics'
  | 'devices';

const EMPTY_TOKEN_STATUS: TokenStatus = {
  github: false,
  anthropic: false,
  fal: false,
  npmjs: false,
  'github-packages': false,
};

const PREVIEW_PHONE_CAP_OPTIONS = [1, 2, 3, 4, 5, 6] as const;
type PreviewPhoneCapSelection = 'auto' | `${(typeof PREVIEW_PHONE_CAP_OPTIONS)[number]}`;

function previewPhoneCapSelectionFromConfig(
  value: CrucibleConfig['previewPhoneCapOverride'],
): PreviewPhoneCapSelection {
  const normalised = normalisePreviewPhoneCapOverride(value);
  return normalised === null ? 'auto' : (`${normalised}` as PreviewPhoneCapSelection);
}

function previewPhoneCapSelectionToConfig(value: PreviewPhoneCapSelection): number | null {
  if (value === 'auto') return null;
  return normalisePreviewPhoneCapOverride(Number(value));
}

export function Settings({
  defaultTab = 'connections',
}: { defaultTab?: SettingsTab } = {}): React.JSX.Element {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<SettingsTab>(defaultTab);
  const { data: tokens = EMPTY_TOKEN_STATUS } = useIpcQuery('tokens:get');
  const { data: config = null } = useIpcQuery('config:get');
  // Maintainer mode is read-once at app start in the main process. We pass
  // it through as a boolean here; non-maintainers don't see the engineer-only
  // registry-token rows.
  const { data: isMaintainer = false } = useIpcQuery('app:is-maintainer');
  const flags = useFeatureFlags();
  const frame = useAppFrame();
  const shellChrome = frame === 'embedded' ? 'web' : 'electron';

  const tabs: { key: SettingsTab; label: string }[] = [
    { key: 'connections', label: 'Connections' },
    { key: 'preferences', label: 'Preferences' },
    { key: 'privacy', label: 'Privacy' },
    { key: 'advanced', label: 'Advanced' },
    { key: 'diagnostics', label: 'Diagnostics' },
    ...(flags.newUi ? [{ key: 'devices' as SettingsTab, label: 'Devices' }] : []),
  ];

  return (
    <div className="flex h-full flex-col">
      <FoundryShellHeader
        chrome={shellChrome}
        title={flags.newUi ? undefined : 'Settings'}
        leading={
          flags.newUi ? (
            <Link
              to="/dashboard"
              className="app-region-no-drag flex h-10 w-10 items-center justify-center rounded-full border border-white/[0.11] transition-all hover:border-white/[0.18]"
              style={{
                background: 'var(--color-crucible-bg)',
                color: 'var(--text-3)',
                transitionTimingFunction: 'var(--ease-flora)',
                transitionDuration: 'var(--motion-fast)',
              }}
              aria-label="Back to Dashboard"
            >
              <Home size={16} strokeWidth={1.75} />
            </Link>
          ) : (
            <>
              <button
                onClick={() => navigate('/dashboard')}
                className="text-sm text-white/40 transition-colors hover:text-white/80"
              >
                &larr; Back
              </button>
              <h1 className="text-xl font-semibold">Settings</h1>
            </>
          )
        }
        center={
          flags.newUi ? (
            <span className="f-title" style={{ color: 'var(--text-1)' }}>
              Settings
            </span>
          ) : undefined
        }
        actions={
          isMaintainer ? (
            <span
              data-testid="maintainer-mode-indicator"
              className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs text-amber-200"
              title="CRUCIBLE_MAINTAINER_MODE=1 — registry-token controls are visible."
            >
              maintainer mode
            </span>
          ) : null
        }
      />

      <div className="flex flex-1 overflow-hidden">
        {/* Tab sidebar */}
        <nav
          className={`${flags.newUi ? 'w-52 px-4 py-6' : 'w-48 p-4'} ${flags.newUi ? '' : 'border-r border-white/10'}`}
          style={flags.newUi ? { borderRight: '1px solid var(--border-alpha-1)' } : undefined}
        >
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`w-full rounded-lg px-3 text-left text-sm transition-colors ${
                flags.newUi
                  ? 'mb-0.5 py-2.5'
                  : `mb-1 py-2 ${
                      activeTab === tab.key
                        ? 'bg-white/10 text-white'
                        : 'text-white/50 hover:bg-white/5 hover:text-white/70'
                    }`
              }`}
              style={
                flags.newUi
                  ? activeTab === tab.key
                    ? {
                        background: 'rgba(255,255,255,0.08)',
                        color: 'var(--text-1)',
                        transitionTimingFunction: 'var(--ease-flora)',
                        transitionDuration: 'var(--motion-fast)',
                      }
                    : {
                        color: 'var(--text-3)',
                        transitionTimingFunction: 'var(--ease-flora)',
                        transitionDuration: 'var(--motion-fast)',
                      }
                  : undefined
              }
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {/* Content */}
        <main className={`flex-1 overflow-auto ${flags.newUi ? 'px-8 py-8' : 'p-6'}`}>
          {activeTab === 'connections' && (
            <ConnectionsPanel tokens={tokens} isMaintainer={isMaintainer} />
          )}

          {activeTab === 'preferences' && <PreferencesPanel config={config} />}

          {activeTab === 'privacy' && <PrivacyPanel />}

          {activeTab === 'advanced' && <AdvancedPanel config={config} />}

          {activeTab === 'diagnostics' && <DiagnosticsPanel />}

          {flags.newUi && activeTab === 'devices' && <DevicesPanel />}
        </main>
      </div>
    </div>
  );
}

function ConnectionsPanel({
  tokens: initialTokens,
  isMaintainer,
}: {
  tokens: TokenStatus;
  isMaintainer: boolean;
}): React.JSX.Element {
  const { newUi } = useFeatureFlags();
  const [editing, setEditing] = useState<'github' | 'anthropic' | 'fal' | null>(null);
  const [tokenValue, setTokenValue] = useState('');
  const [saving, setSaving] = useState(false);
  // Tier 3: keep the structured error so <CrucibleErrorState> can show
  // recovery + code (e.g. EncryptionUnavailableError tells the user to
  // install gnome-keyring; ad-hoc red text dropped that signal).
  const [error, setError] = useState<SerialisedError | null>(null);
  // PR #112: useIpcQuery's `error` surfaces an `EncryptionUnavailableError`
  // thrown by the main process when the OS keychain isn't available. Without
  // checking it explicitly the renderer would silently fall back to
  // `EMPTY_TOKEN_STATUS` and pretend nothing's wrong.
  const { data: tokens = initialTokens, refetch, error: queryError } = useIpcQuery('tokens:get');
  const refreshTokens = (): void => {
    void refetch();
  };
  const encryptionUnavailable =
    queryError instanceof Error &&
    (queryError.message.includes('EncryptionUnavailableError') ||
      queryError.message.includes('keychain') ||
      queryError.message.includes('safeStorage'));

  const queryClient = useQueryClient();
  const handleSave = useCallback(async () => {
    if (!editing || !tokenValue.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const key = editing;
      await window.crucible.invoke('tokens:set', { key, value: tokenValue.trim() });
      // Issue #381: saving a Fal key from Settings should also clear any
      // previous "I declined" sentinel — otherwise the key would be set
      // but the row would still claim "you opted out". The IPC tolerates
      // a redundant clear (no-op when the flag isn't set).
      if (key === 'fal') {
        try {
          await window.crucible.invoke('app:set-fal-key-declined', { declined: false });
          queryClient.invalidateQueries({ queryKey: ['app:fal-key-declined'] });
        } catch {
          // Non-fatal — the key is saved either way; the decline flag
          // is just a UX hint, not part of the auth path.
        }
      }
      setEditing(null);
      setTokenValue('');
      refetch();
    } catch (err: unknown) {
      setError(unflattenIpcError(err));
    } finally {
      setSaving(false);
    }
  }, [editing, tokenValue, refetch, queryClient]);

  const handleRetry = useCallback(() => {
    void handleSave();
  }, [handleSave]);

  return (
    <div className={newUi ? 'space-y-8' : 'space-y-6'}>
      <h2
        className={newUi ? 'f-title' : 'text-lg font-medium'}
        style={newUi ? { color: 'var(--text-1)' } : undefined}
      >
        Connections
      </h2>

      {/* Encryption-unavailable banner — fail-closed if the OS keychain is
          missing. Without this banner the user would see all tokens as
          "not configured" and silently fail to save new ones.

          The copy is intentionally generic ("API keys") so it reads sensibly
          to non-maintainer users who only see the GitHub PAT / Anthropic /
          Fal.ai rows below. The banner still fires for those rows regardless
          of maintainer mode — they all live in the same encrypted store. */}
      {encryptionUnavailable && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">
          <strong className="block font-semibold">Secure storage unavailable</strong>
          <p className="mt-1 text-red-200/80">
            Foundry can't save your API keys securely on this computer because the operating-system
            keychain isn't available. Foundry refuses to fall back to plaintext storage. On Linux,
            install
            <code className="mx-1 rounded bg-red-500/20 px-1">gnome-keyring</code> or
            <code className="mx-1 rounded bg-red-500/20 px-1">libsecret-1-0</code>
            and restart Foundry. On macOS, the system Keychain should be available by default — try
            restarting your computer, or check with an administrator.
          </p>
          {queryError instanceof Error && (
            <p className="mt-2 font-mono text-xs text-red-300/70">{queryError.message}</p>
          )}
        </div>
      )}

      {/* Token input dialog */}
      {editing && (
        <div
          data-testid="token-editor"
          className={`rounded-xl p-4 space-y-3 ${newUi ? '' : 'rounded-lg border border-crucible-accent/50 bg-white/5'}`}
          style={
            newUi
              ? {
                  boxShadow:
                    'inset 0 0 0 1px var(--color-crucible-accent, #2145F7), var(--hairline)',
                  background: 'var(--bg-elev-2)',
                }
              : undefined
          }
        >
          <h3 className="font-medium">
            {editing === 'github'
              ? 'GitHub Token'
              : editing === 'anthropic'
                ? 'Anthropic API Key'
                : 'Fal.ai API Key'}
          </h3>
          <TokenGuide type={editing} />
          <input
            type="password"
            value={tokenValue}
            onChange={(e) => setTokenValue(e.target.value)}
            placeholder={editing === 'github' ? 'ghp_...' : 'sk-ant-...'}
            className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm focus:border-crucible-accent focus:outline-none"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
            }}
          />
          {error && <CrucibleErrorState error={error} onRetry={handleRetry} compact />}
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={saving || !tokenValue.trim()}
              className="rounded-lg bg-crucible-accent px-4 py-1.5 text-sm font-medium text-white disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={() => {
                setEditing(null);
                setTokenValue('');
                setError(null);
              }}
              className="rounded-lg border border-white/10 px-4 py-1.5 text-sm text-white/60 hover:text-white/80"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div
        className={`rounded-xl ${newUi ? 'p-6' : 'p-4 border border-white/10'}`}
        style={newUi ? { boxShadow: 'var(--hairline)', background: 'var(--bg-elev-2)' } : undefined}
      >
        <div className="flex items-center justify-between">
          <div className={newUi ? 'flex flex-col gap-1' : undefined}>
            <h3 className="font-medium" style={newUi ? { color: 'var(--text-1)' } : undefined}>
              GitHub
            </h3>
            <p
              className={newUi ? 'f-body' : 'text-sm text-white/40'}
              style={newUi ? { color: 'var(--text-3)' } : undefined}
            >
              {tokens.github ? '✓ Connected' : 'Not connected'}
            </p>
          </div>
          <div className="flex gap-2">
            {tokens.github && <TokenTestButton kind="github" />}
            <button
              onClick={() => {
                setEditing('github');
                setTokenValue('');
                setError(null);
              }}
              className={`rounded-lg px-3 py-1.5 text-sm ${newUi ? '' : 'border border-white/10 text-white/60 hover:border-white/20 hover:text-white/80'}`}
              style={
                newUi
                  ? {
                      boxShadow: 'var(--hairline)',
                      color: 'var(--text-2)',
                      background: 'var(--bg-elev-3)',
                    }
                  : undefined
              }
            >
              {tokens.github ? 'Update' : 'Connect'}
            </button>
          </div>
        </div>
      </div>

      <div
        className={`rounded-xl ${newUi ? 'p-6' : 'p-4 border border-white/10'}`}
        style={newUi ? { boxShadow: 'var(--hairline)', background: 'var(--bg-elev-2)' } : undefined}
      >
        <div className="flex items-center justify-between">
          <div className={newUi ? 'flex flex-col gap-1' : undefined}>
            <h3 className="font-medium" style={newUi ? { color: 'var(--text-1)' } : undefined}>
              Anthropic AI
            </h3>
            <p
              className={newUi ? 'f-body' : 'text-sm text-white/40'}
              style={newUi ? { color: 'var(--text-3)' } : undefined}
            >
              {tokens.anthropic ? '✓ Connected' : 'Not connected'}
            </p>
            <p
              className={newUi ? 'f-body' : 'mt-1 text-xs text-white/30'}
              style={newUi ? { color: 'var(--text-3)' } : undefined}
            >
              Required for the in-app AI chat panel in the workspace.
            </p>
          </div>
          <div className="flex gap-2">
            {tokens.anthropic && <TokenTestButton kind="anthropic" />}
            <button
              onClick={() => {
                setEditing('anthropic');
                setTokenValue('');
                setError(null);
              }}
              className={`rounded-lg px-3 py-1.5 text-sm ${newUi ? '' : 'border border-white/10 text-white/60 hover:border-white/20 hover:text-white/80'}`}
              style={
                newUi
                  ? {
                      boxShadow: 'var(--hairline)',
                      color: 'var(--text-2)',
                      background: 'var(--bg-elev-3)',
                    }
                  : undefined
              }
            >
              {tokens.anthropic ? 'Update' : 'Connect'}
            </button>
          </div>
        </div>
      </div>

      <FalConnectionRow
        connected={tokens.fal}
        onEdit={() => {
          setEditing('fal');
          setTokenValue('');
          setError(null);
        }}
      />

      {/* Registry tokens — MAINTAINER-ONLY. These are engineer credentials
          for refreshing the hello-weekend template's pinned vendor packages.
          Non-technical users (board-member mode) never see this section.
          The IPC handlers `tokens:test-registry` and the registry-kind
          branch of `tokens:clear` also enforce the gate server-side as
          defence in depth. */}
      {isMaintainer && (
        <RegistryTokensSection tokens={tokens} onChanged={refreshTokens} newUi={newUi} />
      )}
    </div>
  );
}

/**
 * Settings row for the Fal.ai API key (issue #381). Mirrors the GitHub /
 * Anthropic rows above but adds the third state introduced by #381's
 * onboarding flow: the user has explicitly DECLINED to provide a key.
 *
 * Three observable states:
 *   - `connected`                          — key set, "Update" CTA
 *   - `declined && !connected`             — user opted out via onboarding
 *                                            or the toggle below; "Connect"
 *                                            CTA + "you opted out" pill
 *   - `!declined && !connected`            — "Not connected", "Connect" CTA
 *
 * The toggle lets the user un-decline without adding a key (so the
 * onboarding doesn't forever remember a stale "no") and re-decline if they
 * later remove a key (so the onboarding doesn't pop back up next launch).
 *
 * Exported so the renderer SSR test in `Settings.fal.test.tsx` can render
 * each state without dragging in the full router/query providers.
 */
export function FalConnectionRow({
  connected,
  onEdit,
}: {
  connected: boolean;
  onEdit: () => void;
}): React.JSX.Element {
  const { newUi } = useFeatureFlags();
  const queryClient = useQueryClient();
  const { data: declined = false, refetch } = useIpcQuery('app:fal-key-declined');
  const [toggling, setToggling] = useState(false);

  const handleToggleDecline = useCallback(
    async (next: boolean) => {
      if (toggling) return;
      setToggling(true);
      try {
        await window.crucible.invoke('app:set-fal-key-declined', { declined: next });
        void refetch();
        queryClient.invalidateQueries({ queryKey: ['app:fal-key-declined'] });
      } finally {
        setToggling(false);
      }
    },
    [toggling, refetch, queryClient],
  );

  return (
    <div
      className={`${newUi ? 'rounded-xl p-6' : 'rounded-lg border border-white/10 p-4'}`}
      style={newUi ? { boxShadow: 'var(--hairline)', background: 'var(--bg-elev-2)' } : undefined}
      data-testid="fal-connection-row"
    >
      <div className="flex items-center justify-between">
        <div className={newUi ? 'flex flex-col gap-1' : undefined}>
          <h3 className="font-medium" style={newUi ? { color: 'var(--text-1)' } : undefined}>
            Fal.ai
          </h3>
          <p
            className={newUi ? 'f-body' : 'text-sm text-white/40'}
            style={newUi ? { color: 'var(--text-3)' } : undefined}
          >
            {connected
              ? '✓ Connected'
              : declined
                ? 'Using SVG placeholders (you opted out)'
                : 'Not connected'}
          </p>
          <p
            className={newUi ? 'f-body' : 'text-xs text-white/30'}
            style={newUi ? { color: 'var(--text-3)' } : undefined}
          >
            AI artwork generation for game tiles
          </p>
          {connected && (
            <p
              className={newUi ? 'mt-1 f-body' : 'mt-1 text-xs text-white/30'}
              style={newUi ? { color: 'var(--text-3)' } : undefined}
            >
              No live test available — Fal.ai doesn&apos;t expose a whoami endpoint cheap enough to
              use as a verifier. The key is exercised when you generate hero artwork.
            </p>
          )}
        </div>
        <button
          onClick={onEdit}
          className={`rounded-lg px-3 py-1.5 text-sm ${newUi ? '' : 'border border-white/10 text-white/60 hover:border-white/20 hover:text-white/80'}`}
          style={
            newUi
              ? {
                  boxShadow: 'var(--hairline)',
                  color: 'var(--text-2)',
                  background: 'var(--bg-elev-3)',
                }
              : undefined
          }
        >
          {connected ? 'Update' : 'Connect'}
        </button>
      </div>

      {/* Decline toggle — only meaningful when no key is set. Shown
          below the row so the primary "Connect" CTA stays the most
          prominent action. When a key is already configured the toggle
          is hidden because the on-disk key takes precedence; revoking
          it (a future "Remove" affordance, not in this PR) would set
          the decline back. */}
      {!connected && (
        <div className="mt-3 flex items-start gap-2 border-t border-white/5 pt-3">
          <input
            id="fal-decline-toggle"
            type="checkbox"
            checked={declined}
            disabled={toggling}
            onChange={(e) => void handleToggleDecline(e.target.checked)}
            data-testid="fal-decline-toggle"
            className="mt-1 h-3.5 w-3.5 cursor-pointer accent-crucible-accent"
          />
          <label
            htmlFor="fal-decline-toggle"
            className={newUi ? 'cursor-pointer f-body' : 'cursor-pointer text-xs text-white/40'}
            style={newUi ? { color: 'var(--text-3)' } : undefined}
          >
            <span className="block text-white/60">Use SVG placeholders</span>
            <span className="mt-0.5 block">
              Skip the Fal.ai prompt during onboarding. Generated game artwork will fall back to
              bundled SVG placeholders. You can untick this any time to re-enable the prompt.
            </span>
          </label>
        </div>
      )}
    </div>
  );
}

/**
 * Maintainer-only Settings section: the npmjs.org and GitHub Packages
 * token rows that engineers use when refreshing the hello-weekend
 * template's vendor packages. Extracted to a sub-component so the
 * conditional render can be exercised by component tests.
 *
 * Exported for the component-render tests in
 * `src/renderer/__tests__/Settings.gate.test.tsx`.
 */
export function RegistryTokensSection({
  tokens,
  onChanged,
  newUi = false,
}: {
  tokens: TokenStatus;
  onChanged: () => void;
  newUi?: boolean;
}): React.JSX.Element {
  return (
    <div
      className={`pt-6 ${newUi ? '' : 'border-t border-white/10'}`}
      style={
        newUi ? { borderTop: '1px solid var(--border-alpha-1)', paddingTop: '1.5rem' } : undefined
      }
      data-testid="registry-tokens-section"
    >
      <h2
        className={`mb-1 ${newUi ? 'f-title' : 'text-lg font-medium'}`}
        style={newUi ? { color: 'var(--text-1)' } : undefined}
      >
        Registry tokens
      </h2>
      <p
        className={newUi ? 'mb-4 f-body' : 'mb-4 text-sm text-white/40'}
        style={newUi ? { color: 'var(--text-3)' } : undefined}
      >
        Used by Foundry to vendor private{' '}
        <code className="rounded bg-white/10 px-1">@volley/*</code> and{' '}
        <code className="rounded bg-white/10 px-1">@volley-inc/*</code> packages into your games.
        Stored encrypted in your OS keychain.
      </p>
      <div className="space-y-4">
        <RegistryTokenRow
          kind="npmjs"
          title="npmjs.org"
          description="Read-only token for legacy @volley/* packages on npmjs.org."
          placeholder="npm_..."
          configured={tokens.npmjs}
          onChanged={onChanged}
          newUi={newUi}
        />
        <RegistryTokenRow
          kind="github-packages"
          title="GitHub Packages"
          description="Personal access token with read:packages scope for @volley-inc/* packages."
          placeholder="ghp_..."
          configured={tokens['github-packages']}
          onChanged={onChanged}
          newUi={newUi}
        />
      </div>
    </div>
  );
}

/**
 * One row in the "Registry tokens" section. Encapsulates:
 *   - paste box for a new token
 *   - "Test" button that round-trips a whoami call
 *   - "Save" button (only enabled when a value is pasted)
 *   - "Remove" button (only shown when one is already configured)
 *   - "Where do I get this?" expander linking to the relevant docs
 *
 * The stored token is NEVER displayed back to the user. We only show a
 * "Token configured ✓" pill once one is set.
 */
/**
 * Test button for GitHub / Anthropic token rows. User clicks → we call
 * the existing `tokens:verify` IPC (which round-trips both keys against
 * /user and /v1/models respectively, distinguishing 401/403/429/network
 * via `verify-token.ts`) and show the per-token result inline.
 *
 * The verify IPC reads the STORED token, not whatever's in the input
 * box — that's the user-facing question this button answers ("is the
 * key Foundry will actually use working right now?"). Rate-limited to
 * one outstanding request per click; clicks while in-flight are
 * ignored.
 *
 * Skipped for Fal.ai because Fal doesn't expose a whoami-style
 * endpoint that we can call without spending credits — we let the
 * hero-image generation path surface real failures.
 */
function TokenTestButton({ kind }: { kind: 'github' | 'anthropic' }): React.JSX.Element {
  const [state, setState] = useState<
    { kind: 'idle' } | { kind: 'testing' } | { kind: 'done'; detail: TokenVerifyDetail }
  >({ kind: 'idle' });

  const handleTest = useCallback(async () => {
    if (state.kind === 'testing') return;
    setState({ kind: 'testing' });
    try {
      const result = await window.crucible.invoke('tokens:verify');
      const detail = kind === 'github' ? result.github : result.anthropic;
      setState({ kind: 'done', detail });
    } catch (err) {
      setState({
        kind: 'done',
        detail: {
          status: 'invalid',
          reason: 'unexpected',
          error: err instanceof Error ? err.message : String(err),
        },
      });
    }
  }, [kind, state.kind]);

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        onClick={() => void handleTest()}
        disabled={state.kind === 'testing'}
        className="rounded-lg border border-white/10 px-3 py-1.5 text-sm text-white/60 hover:border-white/20 hover:text-white/80 disabled:opacity-50"
      >
        {state.kind === 'testing' ? 'Testing…' : 'Test'}
      </button>
      {state.kind === 'done' && (
        <p
          className={`max-w-[18rem] text-right text-xs ${
            state.detail.status === 'valid' ? 'text-emerald-300' : 'text-red-300'
          }`}
          role="status"
          aria-live="polite"
        >
          {state.detail.status === 'valid'
            ? state.detail.scopes && state.detail.scopes.length > 0
              ? `✓ Valid — scopes: ${state.detail.scopes.join(', ')}`
              : '✓ Valid'
            : state.detail.error
              ? `✗ ${state.detail.error}`
              : `✗ Failed (${state.detail.reason ?? 'unknown'})`}
        </p>
      )}
    </div>
  );
}

function RegistryTokenRow({
  kind,
  title,
  description,
  placeholder,
  configured,
  onChanged,
  newUi = false,
}: {
  kind: RegistryTokenTestKind;
  title: string;
  description: string;
  placeholder: string;
  configured: boolean;
  onChanged: () => void;
  newUi?: boolean;
}): React.JSX.Element {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState<'idle' | 'testing' | 'saving' | 'removing'>('idle');
  const [testResult, setTestResult] = useState<RegistryTokenTestResult | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);

  const trimmed = value.trim();
  const canAct = trimmed.length > 0 && busy === 'idle';

  const handleTest = async (): Promise<void> => {
    if (!canAct) return;
    setBusy('testing');
    setTestResult(null);
    setErrorMsg(null);
    setFeedback(null);
    try {
      const result = await window.crucible.invoke('tokens:test-registry', {
        kind,
        value: trimmed,
      });
      setTestResult(result);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy('idle');
    }
  };

  const handleSave = async (): Promise<void> => {
    if (!canAct) return;
    setBusy('saving');
    setErrorMsg(null);
    setFeedback(null);
    try {
      await window.crucible.invoke('tokens:set', { key: kind, value: trimmed });
      setValue('');
      setTestResult(null);
      setFeedback('Saved.');
      onChanged();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy('idle');
    }
  };

  const handleRemove = async (): Promise<void> => {
    if (busy !== 'idle') return;
    setBusy('removing');
    setErrorMsg(null);
    setFeedback(null);
    try {
      await window.crucible.invoke('tokens:clear', { key: kind });
      setFeedback('Removed.');
      onChanged();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy('idle');
    }
  };

  return (
    <div
      className={`space-y-3 ${newUi ? 'rounded-xl p-6' : 'rounded-lg border border-white/10 p-4'}`}
      style={newUi ? { boxShadow: 'var(--hairline)', background: 'var(--bg-elev-2)' } : undefined}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-medium" style={newUi ? { color: 'var(--text-1)' } : undefined}>
            {title}
          </h3>
          <p
            className={newUi ? 'f-body' : 'text-xs text-white/40'}
            style={newUi ? { color: 'var(--text-3)' } : undefined}
          >
            {description}
          </p>
        </div>
        {configured && (
          <span className="shrink-0 rounded-full bg-green-500/15 px-2 py-0.5 text-xs text-green-300">
            Token configured ✓
          </span>
        )}
      </div>

      <div className="flex gap-2">
        <input
          type="password"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            // A new value invalidates any prior test result — don't let a
            // stale "✓ Verified" linger after the user types a different
            // token.
            setTestResult(null);
            setFeedback(null);
            setErrorMsg(null);
          }}
          placeholder={configured ? `${placeholder}  (paste new value to update)` : placeholder}
          className="flex-1 rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm placeholder-white/30 focus:border-crucible-accent focus:outline-none"
          autoComplete="off"
          spellCheck={false}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleSave();
          }}
        />
        <button
          onClick={() => void handleTest()}
          disabled={!canAct}
          className={`rounded-lg px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-40 ${newUi ? '' : 'border border-white/10 text-white/70 hover:border-white/20 hover:text-white'}`}
          style={
            newUi
              ? {
                  boxShadow: 'var(--hairline)',
                  color: 'var(--text-2)',
                  background: 'var(--bg-elev-3)',
                }
              : undefined
          }
        >
          {busy === 'testing' ? 'Testing…' : 'Test'}
        </button>
        <button
          onClick={() => void handleSave()}
          disabled={!canAct}
          className="rounded-lg bg-crucible-accent px-3 py-2 text-sm font-medium text-white hover:bg-crucible-accent/90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {busy === 'saving' ? 'Saving…' : 'Save'}
        </button>
        {configured && (
          <button
            onClick={() => void handleRemove()}
            disabled={busy !== 'idle'}
            className="rounded-lg border border-red-500/30 px-3 py-2 text-sm text-red-300 hover:border-red-500/50 hover:text-red-200 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy === 'removing' ? 'Removing…' : 'Remove'}
          </button>
        )}
      </div>

      {/* Inline feedback area — verify result, save confirmation, or error */}
      {testResult && testResult.valid && (
        <p className="text-sm text-green-400">
          ✓ Verified{testResult.username ? ` as ${testResult.username}` : ''}
        </p>
      )}
      {testResult && !testResult.valid && (
        <p className="text-sm text-red-400">✗ {testResult.error ?? 'Token rejected.'}</p>
      )}
      {feedback && (
        <p
          className={`text-sm ${newUi ? '' : 'text-white/60'}`}
          style={newUi ? { color: 'var(--text-2)' } : undefined}
        >
          {feedback}
        </p>
      )}
      {errorMsg && <p className="text-sm text-red-400">{errorMsg}</p>}

      <div>
        <button
          onClick={() => setHelpOpen(!helpOpen)}
          className="text-xs text-crucible-accent hover:underline"
        >
          {helpOpen ? 'Hide instructions ▲' : 'Where do I get this? ▼'}
        </button>
        {helpOpen && (
          <div
            className={`mt-2 p-3 text-xs space-y-2 ${newUi ? 'rounded-xl' : 'rounded-lg bg-white/5 text-white/60'}`}
            style={
              newUi
                ? {
                    boxShadow: 'var(--hairline)',
                    background: 'var(--bg-elev-1)',
                    color: 'var(--text-2)',
                  }
                : undefined
            }
          >
            {kind === 'npmjs' ? (
              <>
                <p>
                  Create a <strong className="text-white/80">classic automation token</strong> on
                  npmjs.org with read access to the{' '}
                  <code className="rounded bg-white/10 px-1">@volley</code> scope.
                </p>
                <button
                  onClick={() =>
                    window.open(
                      'https://docs.npmjs.com/creating-and-viewing-access-tokens',
                      '_blank',
                    )
                  }
                  className="text-crucible-accent hover:underline"
                >
                  npm docs: creating and viewing access tokens →
                </button>
              </>
            ) : (
              <>
                <p>
                  Create a <strong className="text-white/80">classic personal access token</strong>{' '}
                  with the <code className="rounded bg-white/10 px-1">read:packages</code> scope,
                  then authorise it for the{' '}
                  <code className="rounded bg-white/10 px-1">Volley-Inc</code> SSO org.
                </p>
                <button
                  onClick={() =>
                    window.open(
                      'https://github.com/settings/tokens/new?scopes=read:packages&description=Foundry%20vendor%20packages',
                      '_blank',
                    )
                  }
                  className="text-crucible-accent hover:underline"
                >
                  Create a token with read:packages pre-selected →
                </button>
              </>
            )}
            <p className="text-white/40">
              Tokens are stored encrypted in your OS keychain. They are never displayed back to you
              after saving — paste a new value to update.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export function PreferencesPanel({ config }: { config: CrucibleConfig | null }): React.JSX.Element {
  const { newUi, livePreview, designTab, loaded: flagsLoaded } = useFeatureFlags();
  const queryClient = useQueryClient();
  const [gamesDir, setGamesDir] = useState(config?.gamesDir ?? '');
  const [githubOrg, setGithubOrg] = useState(config?.githubOrg ?? 'Volley-Inc');
  const [theme, setTheme] = useState(config?.theme ?? 'dark');
  const [showTestGames, setShowTestGames] = useState(config?.showTestGames ?? false);
  // #1408 §9 Phase 0 / #1420 §5.2 — auto-open the live preview during a
  // build. Default ON (opt-out). Only surfaced when the `livePreview`
  // flag is on (below); the value still round-trips harmlessly otherwise.
  const [livePreviewAutoOpen, setLivePreviewAutoOpen] = useState(
    config?.livePreviewAutoOpen ?? true,
  );
  const [designTabEnabled, setDesignTabEnabled] = useState(config?.designTabEnabled ?? true);
  const [designTabSaving, setDesignTabSaving] = useState(false);
  const [designTabSaved, setDesignTabSaved] = useState(false);
  const [designTabError, setDesignTabError] = useState('');
  const [falKey, setFalKey] = useState('');
  const [falSaved, setFalSaved] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Load fal key status
  const { data: tokens } = useIpcQuery('tokens:get');

  useEffect(() => {
    if (config) {
      setGamesDir(config.gamesDir ?? '');
      setGithubOrg(config.githubOrg ?? 'Volley-Inc');
      setTheme(config.theme ?? 'dark');
      setShowTestGames(config.showTestGames ?? false);
      setLivePreviewAutoOpen(config.livePreviewAutoOpen ?? true);
      setDesignTabEnabled(config.designTabEnabled ?? true);
    }
  }, [config]);

  const handleDesignTabToggle = async () => {
    if (!flagsLoaded || !designTab || designTabSaving) return;
    const next = !designTabEnabled;
    setDesignTabEnabled(next);
    setDesignTabSaving(true);
    setDesignTabSaved(false);
    setDesignTabError('');
    try {
      await window.crucible.invoke('config:set', { designTabEnabled: next });
      queryClient.setQueryData<CrucibleConfig | null>(['config:get'], (current) =>
        current ? { ...current, designTabEnabled: next } : current,
      );
      void queryClient.invalidateQueries({ queryKey: ['config:get'] });
      setDesignTabSaved(true);
      setTimeout(() => setDesignTabSaved(false), 2000);
    } catch (err) {
      setDesignTabEnabled(!next);
      setDesignTabError(err instanceof Error ? err.message : 'Failed to save Design setting');
    } finally {
      setDesignTabSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      setError('');
      await window.crucible.invoke('config:set', {
        gamesDir,
        githubOrg,
        theme,
        showTestGames,
        // Only persist the auto-open preference when the feature flag is
        // on — flag-OFF the toggle is hidden, so writing the key would
        // drift the sidecar from its flag-OFF byte-identical baseline.
        ...(livePreview ? { livePreviewAutoOpen } : {}),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    }
  };

  const handleSaveFalKey = async () => {
    if (!falKey.trim()) return;
    setError('');
    try {
      await window.crucible.invoke('tokens:set', { key: 'fal', value: falKey.trim() });
      setFalKey('');
      setFalSaved(true);
      queryClient.invalidateQueries({ queryKey: ['tokens:get'] });
      setTimeout(() => setFalSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save Fal.ai key');
    }
  };

  return (
    <div className={newUi ? 'space-y-8' : 'space-y-6'}>
      <h2
        className={newUi ? 'f-title' : 'text-lg font-medium'}
        style={newUi ? { color: 'var(--text-1)' } : undefined}
      >
        Preferences
      </h2>

      <div className={newUi ? 'space-y-6' : 'space-y-4'}>
        <div>
          <label
            className={newUi ? 'mb-1 block f-body' : 'mb-1 block text-sm text-white/60'}
            style={newUi ? { color: 'var(--text-2)' } : undefined}
          >
            Games folder
          </label>
          <input
            type="text"
            value={gamesDir}
            onChange={(e) => setGamesDir(e.target.value)}
            className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm focus:border-crucible-accent focus:outline-none"
          />
        </div>

        <div>
          <label
            className={newUi ? 'mb-1 block f-body' : 'mb-1 block text-sm text-white/60'}
            style={newUi ? { color: 'var(--text-2)' } : undefined}
          >
            GitHub organisation
          </label>
          <input
            type="text"
            value={githubOrg}
            onChange={(e) => setGithubOrg(e.target.value)}
            className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm focus:border-crucible-accent focus:outline-none"
          />
        </div>

        <div>
          <label
            className={newUi ? 'mb-1 block f-body' : 'mb-1 block text-sm text-white/60'}
            style={newUi ? { color: 'var(--text-2)' } : undefined}
          >
            Theme
          </label>
          <select
            value={theme}
            onChange={(e) => setTheme(e.target.value as 'dark' | 'light' | 'system')}
            className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm focus:border-crucible-accent focus:outline-none"
          >
            <option value="dark">Dark</option>
            <option value="light">Light</option>
            <option value="system">System</option>
          </select>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <label
              className={newUi ? 'block f-body' : 'block text-sm text-white/60'}
              style={newUi ? { color: 'var(--text-2)' } : undefined}
            >
              Show test games
            </label>
            <p
              className={newUi ? 'f-body' : 'text-xs text-white/30'}
              style={newUi ? { color: 'var(--text-3)' } : undefined}
            >
              Display games with the "test" category on the dashboard
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={showTestGames}
            onClick={() => setShowTestGames(!showTestGames)}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
              showTestGames ? 'bg-crucible-accent' : 'bg-white/10'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
                showTestGames ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {/* #1408 §9 Phase 0 / #1420 §5.2 — only shown when the live-preview
            feature flag is on; flag-OFF the whole feature is a no-op so the
            toggle would be meaningless. Default ON (opt-out). */}
        {livePreview && (
          <div className="flex items-center justify-between">
            <div>
              <label
                className={newUi ? 'block f-body' : 'block text-sm text-white/60'}
                style={newUi ? { color: 'var(--text-2)' } : undefined}
              >
                Open preview automatically during builds
              </label>
              <p
                className={newUi ? 'f-body' : 'text-xs text-white/30'}
                style={newUi ? { color: 'var(--text-3)' } : undefined}
              >
                Auto-open the live preview when a build starts so you can watch the game assemble
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={livePreviewAutoOpen}
              onClick={() => setLivePreviewAutoOpen(!livePreviewAutoOpen)}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                livePreviewAutoOpen ? 'bg-crucible-accent' : 'bg-white/10'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
                  livePreviewAutoOpen ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        )}

        <div className="flex items-center justify-between gap-4">
          <div>
            <label
              className={newUi ? 'block f-body' : 'block text-sm text-white/60'}
              style={newUi ? { color: 'var(--text-2)' } : undefined}
            >
              Enable Design workspace
            </label>
            <p
              className={newUi ? 'f-body' : 'text-xs text-white/30'}
              style={newUi ? { color: 'var(--text-3)' } : undefined}
            >
              {flagsLoaded && !designTab
                ? 'Design is disabled for this build by CRUCIBLE_DESIGN_TAB=0.'
                : 'Show the Design tab and route build-plan review into Design. Turn this off if Design is causing problems.'}
            </p>
            {(designTabSaved || designTabError) && (
              <p className={`mt-1 text-xs ${designTabError ? 'text-red-400' : 'text-emerald-300'}`}>
                {designTabError || 'Design setting saved.'}
              </p>
            )}
          </div>
          <button
            type="button"
            role="switch"
            aria-label="Enable Design workspace"
            aria-checked={designTabEnabled}
            disabled={!flagsLoaded || !designTab || designTabSaving}
            onClick={() => {
              void handleDesignTabToggle();
            }}
            className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors ${
              designTabEnabled ? 'bg-crucible-accent' : 'bg-white/10'
            } ${
              !flagsLoaded || !designTab || designTabSaving
                ? 'cursor-not-allowed opacity-50'
                : 'cursor-pointer'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
                designTabEnabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          className="rounded-lg bg-crucible-accent px-4 py-2 text-sm font-medium text-white hover:bg-crucible-accent/90"
        >
          {saved ? '✓ Saved' : 'Save Preferences'}
        </button>
        {error && <span className="text-sm text-red-400">{error}</span>}
      </div>

      {/* Fal.ai API key — for AI artwork generation */}
      <div
        className={`pt-6 ${newUi ? '' : 'border-t border-white/10'}`}
        style={
          newUi ? { borderTop: '1px solid var(--border-alpha-1)', paddingTop: '1.5rem' } : undefined
        }
      >
        <h3 className="mb-1 font-medium" style={newUi ? { color: 'var(--text-1)' } : undefined}>
          AI Artwork
        </h3>
        <p
          className={newUi ? 'mb-3 f-body' : 'mb-3 text-xs text-white/40'}
          style={newUi ? { color: 'var(--text-3)' } : undefined}
        >
          Fal.ai generates game tile artwork. {tokens?.fal ? '✓ Key saved.' : 'No key set yet.'}
        </p>
        <div className="flex gap-2">
          <input
            type="password"
            value={falKey}
            onChange={(e) => setFalKey(e.target.value)}
            placeholder={tokens?.fal ? '••••••••  (update key)' : 'Paste your Fal.ai API key'}
            className="flex-1 rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm placeholder-white/30 focus:border-crucible-accent focus:outline-none"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSaveFalKey();
            }}
          />
          <button
            onClick={handleSaveFalKey}
            disabled={!falKey.trim()}
            className="rounded-lg bg-crucible-accent px-4 py-2 text-sm font-medium text-white hover:bg-crucible-accent/90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {falSaved ? '✓ Saved' : 'Save Key'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Curated list of Claude models surfaced in the Settings dropdown.
 * The model field still accepts free-form strings (Anthropic ships
 * SDK-internal aliases, dated revisions, alpha tags), so users can
 * paste anything via the "Custom..." option below; this list just
 * gives the common cases a one-click affordance and avoids users
 * having to remember the exact alias slug.
 *
 * Order: the recommended default first (Opus 4.8 — the resilience
 * default per fix/default-model-opus48; Fable 5 being disabled
 * mid-campaign took the whole fleet down, so the default no longer
 * hard-depends on it), then Fable 5 (most capable, explicit opt-in),
 * then the balanced/cheaper Sonnet, then the "fastest / cheapest"
 * Haiku. Keep this list small — adding everything
 * Anthropic publishes makes the dropdown a model-zoo and pushes
 * users towards over-engineering.
 */
const CLAUDE_MODEL_PRESETS: ReadonlyArray<{ id: string; label: string; hint: string }> = [
  {
    id: 'claude-opus-4-8',
    label: 'Opus 4.8',
    hint: 'Default — highly capable, GA, Opus-tier priced. Resilient to a Fable outage.',
  },
  {
    id: 'claude-fable-5',
    label: 'Fable 5',
    hint: 'Most capable. Best for the hardest builds. Slower, pricier, and outage-prone.',
  },
  {
    id: 'claude-sonnet-4-6',
    label: 'Sonnet 4.6',
    hint: 'Balanced — faster and cheaper than Opus, still strong for most chat.',
  },
  {
    // The Anthropic API publishes Haiku 4.5 only under its dated
    // form (`claude-haiku-4-5-20251001`) — the un-suffixed alias
    // `claude-haiku-4-5` 404s on `/v1/models`. Verified against the
    // model list 2026-05-01 alongside the user's bad-key report.
    id: 'claude-haiku-4-5-20251001',
    label: 'Haiku 4.5',
    hint: 'Fastest and cheapest — small edits and chatty Q&A.',
  },
];

const CUSTOM_MODEL_SENTINEL = '__custom__';

/**
 * Parse a Settings input string into the value to send on the
 * `config:set` IPC. Empty / whitespace-only → `null` (clear the
 * override and revert to the adapter's default). Invalid number →
 * `undefined` (don't include in the partial; the previous saved
 * value is preserved). Otherwise the parsed integer.
 *
 * Range validation is intentionally NOT performed here — the schema
 * enforces 30..500 / 5..100 server-side and the adapter clamps as
 * belt-and-braces. Surfacing a validation error inline to the user
 * is the catch path's job.
 */
function parseOptionalInt(input: string): number | null | undefined {
  const trimmed = input.trim();
  if (trimmed === '') return null;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function parseOptionalNumber(input: string): number | null | undefined {
  const trimmed = input.trim();
  if (trimmed === '') return null;
  const parsed = Number.parseFloat(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

// Exported so __tests__/Settings.component-template.test.tsx can pin
// the Phase -1 read-only row's render behaviour. Existing internal
// callers (the tab dispatch above) keep working unchanged.
export function AdvancedPanel({ config }: { config: CrucibleConfig | null }): React.JSX.Element {
  const { newUi } = useFeatureFlags();
  // Initialise from saved config; if the saved value isn't one of
  // the presets, treat it as a custom override and unmask the text
  // input. The presets array drives the dropdown options; the
  // text input is the source of truth for the actual saved value.
  const initialModel = config?.claudeModel ?? CLAUDE_MODEL_PRESETS[0]!.id;
  const initialIsCustom = !CLAUDE_MODEL_PRESETS.some((m) => m.id === initialModel);
  const [claudeModel, setClaudeModel] = useState(initialModel);
  const [modelSelection, setModelSelection] = useState<string>(
    initialIsCustom ? CUSTOM_MODEL_SENTINEL : initialModel,
  );
  const [logLevel, setLogLevel] = useState(config?.logLevel ?? 'info');
  const [displayPort, setDisplayPort] = useState(config?.devPorts?.display ?? 5173);
  const [controllerPort, setControllerPort] = useState(config?.devPorts?.controller ?? 5174);
  const [serverPort, setServerPort] = useState(config?.devPorts?.server ?? 3100);
  // Build-game interview telemetry toggle (Q5 sign-off, PR #1 of the
  // interview-v2 rollout). Defaults to true (on). Flipping off stops
  // future events from firing once PR #5 wires the emit sites; it has
  // no behavioural effect today since no events exist yet, but the
  // off-switch ships first so the on-by-default policy is honourable
  // from alpha.37 onward.
  const [telemetryBuildGame, setTelemetryBuildGame] = useState(config?.telemetryBuildGame ?? true);
  const [previewPhoneCapOverride, setPreviewPhoneCapOverride] = useState<PreviewPhoneCapSelection>(
    previewPhoneCapSelectionFromConfig(config?.previewPhoneCapOverride),
  );
  // Phase 3 budget overrides (issue tracker — plan doc:
  // docs/plan-big-work-and-agents-ui.md). Empty string = "no
  // override" → main writes `null` to clear and the adapter falls
  // back to its built-in default. A non-empty value gets parsed as
  // a number and clamped server-side by both the schema validator
  // and the adapter. Defaults shown as placeholder hints so users
  // know what they're overriding.
  const [maxTurnsOverride, setMaxTurnsOverride] = useState<string>(
    config?.maxTurnsOverride !== undefined ? String(config.maxTurnsOverride) : '',
  );
  const [maxBudgetUsdOverride, setMaxBudgetUsdOverride] = useState<string>(
    config?.maxBudgetUsdOverride !== undefined ? String(config.maxBudgetUsdOverride) : '',
  );
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (config) {
      const next = config.claudeModel ?? CLAUDE_MODEL_PRESETS[0]!.id;
      setClaudeModel(next);
      setModelSelection(
        CLAUDE_MODEL_PRESETS.some((m) => m.id === next) ? next : CUSTOM_MODEL_SENTINEL,
      );
      setLogLevel(config.logLevel ?? 'info');
      setDisplayPort(config.devPorts?.display ?? 5173);
      setControllerPort(config.devPorts?.controller ?? 5174);
      setServerPort(config.devPorts?.server ?? 3100);
      setTelemetryBuildGame(config.telemetryBuildGame ?? true);
      setPreviewPhoneCapOverride(
        previewPhoneCapSelectionFromConfig(config.previewPhoneCapOverride),
      );
      setMaxTurnsOverride(
        config.maxTurnsOverride !== undefined ? String(config.maxTurnsOverride) : '',
      );
      setMaxBudgetUsdOverride(
        config.maxBudgetUsdOverride !== undefined ? String(config.maxBudgetUsdOverride) : '',
      );
    }
  }, [config]);

  const handleModelSelectionChange = (value: string): void => {
    setModelSelection(value);
    if (value === CUSTOM_MODEL_SENTINEL) {
      // Switching to Custom keeps whatever model is already in the
      // text field — typically a preset slug the user can edit. If
      // they're coming from a preset and the text field had been
      // hidden, the existing claudeModel state IS the preset id, so
      // they see it pre-populated.
      return;
    }
    setClaudeModel(value);
  };

  const selectedPreset = CLAUDE_MODEL_PRESETS.find((m) => m.id === modelSelection);

  const handleSave = async () => {
    try {
      setError('');
      // Empty input → null on the wire = "clear the override".
      // Non-empty + valid number → set the override. Reviewer N1:
      // pre-validate client-side so out-of-bounds values surface a
      // friendly inline error before the IPC round-trip lands a
      // raw Zod stack trace in the user's face.
      const turnsValue = parseOptionalInt(maxTurnsOverride);
      const budgetValue = parseOptionalNumber(maxBudgetUsdOverride);

      if (turnsValue !== null && turnsValue !== undefined) {
        if (turnsValue < 30 || turnsValue > 500) {
          setError('Max turns must be between 30 and 500.');
          return;
        }
      } else if (turnsValue === undefined && maxTurnsOverride.trim() !== '') {
        // Non-empty input that didn't parse (e.g. "abc"). The
        // input is `type=number` so this shouldn't normally happen,
        // but defensive against paste-shenanigans.
        setError('Max turns must be a number.');
        return;
      }

      if (budgetValue !== null && budgetValue !== undefined) {
        if (budgetValue < 5 || budgetValue > 500) {
          setError('Max budget USD must be between 5 and 500.');
          return;
        }
      } else if (budgetValue === undefined && maxBudgetUsdOverride.trim() !== '') {
        setError('Max budget USD must be a number.');
        return;
      }

      await window.crucible.invoke('config:set', {
        claudeModel,
        logLevel,
        devPorts: { display: displayPort, controller: controllerPort, server: serverPort },
        telemetryBuildGame,
        previewPhoneCapOverride: previewPhoneCapSelectionToConfig(previewPhoneCapOverride),
        maxTurnsOverride: turnsValue,
        maxBudgetUsdOverride: budgetValue,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    }
  };

  return (
    <div className={newUi ? 'space-y-8' : 'space-y-6'}>
      <h2
        className={newUi ? 'f-title' : 'text-lg font-medium'}
        style={newUi ? { color: 'var(--text-1)' } : undefined}
      >
        Advanced
      </h2>

      <div className={newUi ? 'space-y-6' : 'space-y-4'}>
        <div>
          <label
            className={newUi ? 'mb-1 block f-body' : 'mb-1 block text-sm text-white/60'}
            style={newUi ? { color: 'var(--text-2)' } : undefined}
          >
            Claude model
          </label>
          <select
            value={modelSelection}
            onChange={(e) => handleModelSelectionChange(e.target.value)}
            aria-label="Claude model preset"
            className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm focus:border-crucible-accent focus:outline-none"
          >
            {CLAUDE_MODEL_PRESETS.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label} ({m.id})
              </option>
            ))}
            <option value={CUSTOM_MODEL_SENTINEL}>Custom (paste a model ID)…</option>
          </select>
          {selectedPreset && (
            <p
              className={newUi ? 'mt-1 f-body' : 'mt-1 text-xs text-white/40'}
              style={newUi ? { color: 'var(--text-3)' } : undefined}
            >
              {selectedPreset.hint}
            </p>
          )}
          {modelSelection === CUSTOM_MODEL_SENTINEL && (
            <input
              type="text"
              value={claudeModel}
              onChange={(e) => setClaudeModel(e.target.value)}
              placeholder="e.g. claude-sonnet-4-5-20250929"
              aria-label="Custom Claude model ID"
              className="mt-2 w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm focus:border-crucible-accent focus:outline-none"
            />
          )}
        </div>

        {/* Ingot-system flag — READ-ONLY surface. Renders a single line
            "Component template: ON (source: default)" so the engineer can
            confirm which precedence layer produced the resolved value. As
            of the v1.0 ship (crucible#1956) the system is ON by default;
            the disable path is the CRUCIBLE_COMPONENT_TEMPLATE=0 kill-switch
            (forceFor still pins per-user). No toggle here — env/CLI own the
            precedence. Hidden entirely when the field is undefined (a
            pre-flag main process that doesn't surface it yet) so the section
            stays tidy on unbumped GUIs. See hello-weekend
            `docs/ingots/phase-minus-1-primer.md` §4.5. */}
        {config?.componentTemplate && (
          <div data-testid="component-template-row">
            <label
              className={newUi ? 'mb-1 block f-body' : 'mb-1 block text-sm text-white/60'}
              style={newUi ? { color: 'var(--text-2)' } : undefined}
            >
              Component template (Ingot system)
            </label>
            <div
              className={
                newUi
                  ? 'rounded-lg border border-white/10 bg-white/5 px-4 py-2 f-body'
                  : 'rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/80'
              }
              style={newUi ? { color: 'var(--text-1)' } : undefined}
            >
              {config.componentTemplate.enabled ? 'ON' : 'OFF'}{' '}
              <span
                className={newUi ? 'f-body' : 'text-xs text-white/40'}
                style={newUi ? { color: 'var(--text-3)' } : undefined}
                data-testid="component-template-source"
              >
                (source: {config.componentTemplate.source})
              </span>
            </div>
            <p
              className={newUi ? 'mt-1 f-body' : 'mt-1 text-xs text-white/40'}
              style={newUi ? { color: 'var(--text-3)' } : undefined}
            >
              Read-only. The Ingot system is ON by default as of the v1.0 ship (crucible#1956).
              Disable it for a single run with{' '}
              <code className="text-xs">CRUCIBLE_COMPONENT_TEMPLATE=0</code> (kill-switch), or pin
              per-user via{' '}
              <code className="text-xs">
                crucible config set componentTemplate.forceFor.append &lt;login&gt;
              </code>
              .
            </p>
          </div>
        )}

        <div>
          <label
            className={newUi ? 'mb-1 block f-body' : 'mb-1 block text-sm text-white/60'}
            style={newUi ? { color: 'var(--text-2)' } : undefined}
          >
            Log level
          </label>
          <select
            value={logLevel}
            onChange={(e) => setLogLevel(e.target.value as 'debug' | 'info' | 'warn' | 'error')}
            className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm focus:border-crucible-accent focus:outline-none"
          >
            <option value="debug">Debug</option>
            <option value="info">Info</option>
            <option value="warn">Warn</option>
            <option value="error">Error</option>
          </select>
        </div>

        <div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={telemetryBuildGame}
              onChange={(e) => setTelemetryBuildGame(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-white/20 bg-white/5 text-crucible-accent focus:ring-crucible-accent"
              data-testid="telemetry-build-game-toggle"
            />
            <span className="flex flex-col gap-0.5">
              <span
                className={newUi ? 'f-body' : 'text-sm text-white/80'}
                style={newUi ? { color: 'var(--text-1)' } : undefined}
              >
                Send anonymous build-game telemetry
              </span>
              <span
                className={newUi ? 'f-body' : 'text-xs text-white/40'}
                style={newUi ? { color: 'var(--text-3)' } : undefined}
              >
                Foundry can record when an interview starts, when each question is answered, and
                when it completes — just timings + agent / question ids, never the question text or
                your answers. Used to measure completion rates and spot stuck flows.
              </span>
            </span>
          </label>
        </div>

        <div>
          <label
            className={newUi ? 'mb-1 block f-body' : 'mb-1 block text-sm text-white/60'}
            style={newUi ? { color: 'var(--text-2)' } : undefined}
          >
            Preview phone cap
          </label>
          <p
            className={newUi ? 'mb-2 f-body' : 'mb-2 text-xs text-white/40'}
            style={newUi ? { color: 'var(--text-3)' } : undefined}
          >
            Auto follows the game's player-count metadata. Raise this when a build needs extra
            controller phones before the metadata catches up.
          </p>
          <select
            value={previewPhoneCapOverride}
            onChange={(e) => setPreviewPhoneCapOverride(e.target.value as PreviewPhoneCapSelection)}
            aria-label="Preview phone cap"
            data-testid="preview-phone-cap-override"
            className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm focus:border-crucible-accent focus:outline-none"
          >
            <option value="auto">Auto (use game player count)</option>
            {PREVIEW_PHONE_CAP_OPTIONS.map((count) => (
              <option key={count} value={count}>
                Up to {count} phone{count === 1 ? '' : 's'}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            className={newUi ? 'mb-1 block f-body' : 'mb-1 block text-sm text-white/60'}
            style={newUi ? { color: 'var(--text-2)' } : undefined}
          >
            Per-message agent ceilings
          </label>
          <p
            className={newUi ? 'mb-2 f-body' : 'mb-2 text-xs text-white/40'}
            style={newUi ? { color: 'var(--text-3)' } : undefined}
          >
            Power-user overrides for the AI agent's per-message turn loop and USD budget. Leave
            blank to use Foundry's defaults (200 turns, $500). Bounded 30–500 turns, $5–$500. Higher
            values let particularly big build flows finish without hitting the cap; lower values
            tighten cost on a model you don't fully trust yet.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span
                className={newUi ? 'f-body' : 'text-xs text-white/40'}
                style={newUi ? { color: 'var(--text-3)' } : undefined}
              >
                Max turns (default 200)
              </span>
              <input
                type="number"
                min={30}
                max={500}
                step={1}
                inputMode="numeric"
                value={maxTurnsOverride}
                onChange={(e) => setMaxTurnsOverride(e.target.value)}
                placeholder="200"
                aria-label="Max turns override"
                className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm focus:border-crucible-accent focus:outline-none"
              />
            </div>
            <div>
              <span
                className={newUi ? 'f-body' : 'text-xs text-white/40'}
                style={newUi ? { color: 'var(--text-3)' } : undefined}
              >
                Max budget USD (default $500)
              </span>
              <input
                type="number"
                min={5}
                max={500}
                step={1}
                value={maxBudgetUsdOverride}
                onChange={(e) => setMaxBudgetUsdOverride(e.target.value)}
                placeholder="500"
                aria-label="Max budget USD override"
                className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm focus:border-crucible-accent focus:outline-none"
              />
            </div>
          </div>
        </div>

        <div>
          <label
            className={newUi ? 'mb-1 block f-body' : 'mb-1 block text-sm text-white/60'}
            style={newUi ? { color: 'var(--text-2)' } : undefined}
          >
            Dev ports
          </label>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <span
                className={newUi ? 'f-body' : 'text-xs text-white/40'}
                style={newUi ? { color: 'var(--text-3)' } : undefined}
              >
                Display
              </span>
              <input
                type="number"
                value={displayPort}
                onChange={(e) => setDisplayPort(Number(e.target.value))}
                className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm focus:border-crucible-accent focus:outline-none"
              />
            </div>
            <div>
              <span
                className={newUi ? 'f-body' : 'text-xs text-white/40'}
                style={newUi ? { color: 'var(--text-3)' } : undefined}
              >
                Controller
              </span>
              <input
                type="number"
                value={controllerPort}
                onChange={(e) => setControllerPort(Number(e.target.value))}
                className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm focus:border-crucible-accent focus:outline-none"
              />
            </div>
            <div>
              <span
                className={newUi ? 'f-body' : 'text-xs text-white/40'}
                style={newUi ? { color: 'var(--text-3)' } : undefined}
              >
                Server
              </span>
              <input
                type="number"
                value={serverPort}
                onChange={(e) => setServerPort(Number(e.target.value))}
                className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm focus:border-crucible-accent focus:outline-none"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          className="rounded-lg bg-crucible-accent px-4 py-2 text-sm font-medium text-white hover:bg-crucible-accent/90"
        >
          {saved ? '✓ Saved' : 'Save Advanced Settings'}
        </button>
        {error && <span className="text-sm text-red-400">{error}</span>}
      </div>

      {/* Furnace (cloud-VM v1) engineer onboarding status. Read-only
          surface; mutation lives in `crucible cohort allowlist add` on
          the operator side. See `docs/onboarding-furnace.md`. */}
      <FurnaceStatusSection />

      {/* About */}
      <AboutSection />
    </div>
  );
}

/**
 * Settings → Advanced → Furnace status.
 *
 * Renderer-side mirror of `app:get-furnace-status`. Read-only. Surfaces
 * whether the engineer's Foundry is wired into the Furnace (cloud-VM v1)
 * rollout — the `CRUCIBLE_CLOUD_VM_V1` flag state, cohort membership for
 * the resolved user identity, and a pointer to the onboarding doc.
 *
 * Flag-OFF / not-in-cohort states render an "opt-in pending" surface
 * rather than a broken half-built one (per the cloud-VM rollout
 * discipline — flag-OFF behaviour must be unchanged from the
 * pre-rollout baseline).
 *
 * Exported so `Settings.furnace.test.tsx` can SSR each state without
 * dragging in the full router/query providers. Production callers
 * mount it from `AdvancedPanel`.
 */
export function FurnaceStatusSection(): React.JSX.Element {
  const { newUi } = useFeatureFlags();
  const { data: status, error, isLoading, refetch } = useIpcQuery('app:get-furnace-status');

  const handleRefresh = (): void => {
    void refetch();
  };

  return (
    <div
      className={`pt-6 ${newUi ? '' : 'border-t border-white/10'}`}
      style={
        newUi ? { borderTop: '1px solid var(--border-alpha-1)', paddingTop: '1.5rem' } : undefined
      }
      data-testid="furnace-status-section"
    >
      <h3 className="mb-1 font-medium" style={newUi ? { color: 'var(--text-1)' } : undefined}>
        Furnace status
      </h3>
      <p
        className={newUi ? 'mb-4 f-body' : 'mb-4 text-xs text-white/40'}
        style={newUi ? { color: 'var(--text-3)' } : undefined}
      >
        Furnace builds run your agent loop on a sandboxed VM pod so it doesn&rsquo;t occupy your
        laptop. The rollout is cohort-gated — engineers are added one at a time by the rollout
        owner. Out of cohort, your builds run locally exactly as they always have.
      </p>

      {isLoading && (
        <p className="text-sm text-white/40" role="status" aria-live="polite">
          Loading Furnace status…
        </p>
      )}

      {error && (
        <p className="text-sm text-red-400" data-testid="furnace-status-error">
          Could not load Furnace status. Try again or check Settings → Diagnostics.
        </p>
      )}

      {status && (
        <div
          className={`space-y-3 ${newUi ? 'rounded-xl p-6' : 'rounded-lg border border-white/10 p-4'}`}
          style={
            newUi ? { boxShadow: 'var(--hairline)', background: 'var(--bg-elev-2)' } : undefined
          }
        >
          <FurnaceStatusRow
            label="Cohort"
            valueTestId="furnace-cohort-value"
            value={cohortLabel(status.cohort)}
            tone={cohortTone(status.cohort)}
            hint={cohortHint(status)}
            newUi={newUi}
          />
          <FurnaceStatusRow
            label="Furnace flag"
            valueTestId="furnace-flag-value"
            value={
              status.cloudVmV1Enabled
                ? 'CRUCIBLE_CLOUD_VM_V1=1 — flag is set'
                : 'CRUCIBLE_CLOUD_VM_V1 not set — your builds run locally'
            }
            tone={status.cloudVmV1Enabled ? 'positive' : 'neutral'}
            hint={
              status.cloudVmV1Enabled
                ? 'Cohort membership AND the closed Phase 0b loop are also required before a build actually routes to a Furnace. Read the onboarding guide for the current rollout state.'
                : undefined
            }
            newUi={newUi}
          />
          <FurnaceStatusRow
            label="Last build target"
            valueTestId="furnace-last-build-target-value"
            value={lastBuildTargetLabel(status.lastBuildTarget)}
            tone="neutral"
            hint="Cross-build target tracking lands in a follow-up release."
            newUi={newUi}
          />
          {status.userId !== null && (
            <FurnaceStatusRow
              label="User identity"
              valueTestId="furnace-user-id-value"
              value={status.userId}
              tone="neutral"
              hint="Used for the cohort consult — share this when asking the rollout owner to add you."
              newUi={newUi}
            />
          )}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          onClick={handleRefresh}
          className={`rounded-lg px-3 py-1.5 text-xs ${newUi ? '' : 'border border-white/10 text-white/70 hover:bg-white/5'}`}
          style={
            newUi
              ? {
                  boxShadow: 'var(--hairline)',
                  color: 'var(--text-2)',
                  background: 'var(--bg-elev-3)',
                }
              : undefined
          }
          data-testid="furnace-refresh-button"
        >
          Re-check
        </button>
        <button
          onClick={() =>
            window.open(
              'https://github.com/Volley-Inc/crucible/blob/main/docs/onboarding-furnace.md',
              '_blank',
            )
          }
          className="text-xs text-crucible-accent hover:underline"
          data-testid="furnace-onboarding-link"
        >
          Read the onboarding guide →
        </button>
        <span className="text-xs text-white/30">Issues? Post in #foundry on Slack.</span>
      </div>
    </div>
  );
}

function cohortLabel(cohort: 'in' | 'out' | 'unknown'): string {
  switch (cohort) {
    case 'in':
      return 'In cohort';
    case 'out':
      return 'Not in cohort';
    case 'unknown':
      return 'Status unknown';
  }
}

function cohortTone(cohort: 'in' | 'out' | 'unknown'): 'positive' | 'neutral' | 'warning' {
  switch (cohort) {
    case 'in':
      return 'positive';
    case 'out':
      return 'warning';
    case 'unknown':
      return 'neutral';
  }
}

/**
 * Cohort hint copy. The "unknown" state has two distinct shapes
 * the panel can land in:
 *
 *   - No userId AND flag off / no identity      — onboarding path.
 *   - Has userId BUT cohort consult failed      — operator triage path.
 *
 * Bugbot caught the original implementation conflating them: every
 * "unknown" rendered the onboarding-path hint, even when a userId
 * was present and the consult itself had failed. Splitting on
 * `userId` keeps the hint copy honest in both cases.
 */
function cohortHint(status: FurnaceStatus): string {
  switch (status.cohort) {
    case 'in':
      return 'Furnace builds are available the next time the rollout loop closes end-to-end.';
    case 'out':
      return 'Ping the rollout owner in #foundry on Slack to be added. Restart Foundry after enrolment.';
    case 'unknown':
      if (status.userId !== null) {
        // Identity resolved, but the cohort consult itself failed
        // (e.g. malformed allowlist file, permission denied). The
        // engineer's actionable path is to ask the operator to
        // re-check the allowlist file — NOT to opt in.
        return 'User identity resolved, but the cohort consult could not run. Post in #foundry on Slack so the rollout owner can check the allowlist file.';
      }
      return 'Either the flag is off or no user identity resolved. Read the onboarding guide for the opt-in path.';
  }
}

function lastBuildTargetLabel(target: 'local' | 'furnace' | null): string {
  if (target === 'local') return 'local';
  if (target === 'furnace') return 'furnace';
  return '— (not tracked yet)';
}

/**
 * Compact row used by `<FurnaceStatusSection>`. Two-line layout:
 * `label` + (optional) `hint` on the left, `value` on the right.
 *
 * Tone drives the value-colour: `positive` = green, `warning` = amber,
 * `neutral` = the default soft white. The semantics are
 * informational-only; the panel is read-only, so the colour does NOT
 * encode an action the user can take from here.
 */
function FurnaceStatusRow({
  label,
  value,
  valueTestId,
  hint,
  tone,
  newUi = false,
}: {
  label: string;
  value: string;
  valueTestId: string;
  hint?: string;
  tone: 'positive' | 'neutral' | 'warning';
  newUi?: boolean;
}): React.JSX.Element {
  const toneClass =
    tone === 'positive'
      ? 'text-emerald-300'
      : tone === 'warning'
        ? 'text-amber-300'
        : 'text-white/80';

  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p className={newUi ? 'f-body text-white/60' : 'text-sm text-white/60'}>{label}</p>
        {hint && (
          <p className={newUi ? 'mt-0.5 f-body text-white/40' : 'mt-0.5 text-xs text-white/40'}>
            {hint}
          </p>
        )}
      </div>
      <p
        className={
          newUi
            ? `shrink-0 text-right f-body ${toneClass}`
            : `shrink-0 text-right text-sm ${toneClass}`
        }
        data-testid={valueTestId}
      >
        {value}
      </p>
    </div>
  );
}

/**
 * Settings → Privacy panel (issue #993).
 *
 * Exposes the Foundry analytics opt-out toggle. The toggle is
 * labelled positively ("Send anonymous analytics…") and defaults to
 * checked — opt-IN — so the user opts out by UNCHECKING. This
 * mirrors the GDPR-friendly "default opt-in, explicit opt-out"
 * pattern used by the broader Volley/Weekend tooling.
 *
 * Wire shape:
 *   - On mount:     read state via `app:get-analytics-opt-out` IPC.
 *   - On toggle:    write via `app:set-analytics-opt-out` IPC. The
 *                   write uses the atomic-write helper inside main
 *                   so a crash mid-save doesn't half-update the file.
 *   - Takes effect: NEXT app launch. The foundry-tracker singleton
 *                   is initialised at boot and we do not hot-reload
 *                   it. The renderer surfaces this in the copy
 *                   below the checkbox.
 *
 * Exported so the renderer SSR test (`Settings.privacy.test.tsx`) can
 * render the panel without dragging in the full router/query
 * providers.
 */
export function PrivacyPanel(): React.JSX.Element {
  const { newUi } = useFeatureFlags();
  const { data: optOut = false, refetch } = useIpcQuery('app:get-analytics-opt-out');
  // Local UI state — the checkbox shows "send analytics" (inverse
  // of opt-out). `sendAnalytics === true` means we are opted IN
  // (`optOut === false`); `sendAnalytics === false` means opted OUT.
  const sendAnalytics = !optOut;
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // Bugbot round-1 (medium): the IPC bridge serialises thrown errors
  // as `[CRUCIBLE_ERR_V1]` envelopes; rendering them with
  // `err.message` shows raw wire format to the user. Use the same
  // `unflattenIpcError` pattern the Connections / Diagnostics
  // handlers use elsewhere in this file, and render via the shared
  // `<CrucibleErrorState>` component so the user sees the friendly
  // message + recovery suggestion.
  const [error, setError] = useState<SerialisedError | null>(null);
  // Bugbot round-2 (medium): the retry handler can't compute the
  // intended write from current state — `sendAnalytics` reflects
  // the server state (never updated on failure), so `!sendAnalytics`
  // on retry would write the existing value (a no-op that lies
  // "Saved"). Pin the user's intent into the failed `pendingOptOut`
  // here so retry re-fires the exact write the user originally
  // clicked. Cleared on success.
  const [pendingOptOut, setPendingOptOut] = useState<boolean | null>(null);
  const queryClient = useQueryClient();

  const performToggle = useCallback(
    async (intendedOptOut: boolean) => {
      setSaving(true);
      setError(null);
      setSaved(false);
      try {
        await window.crucible.invoke('app:set-analytics-opt-out', {
          optOut: intendedOptOut,
        });
        await refetch();
        queryClient.invalidateQueries({ queryKey: ['app:get-analytics-opt-out'] });
        setPendingOptOut(null);
        setSaved(true);
        window.setTimeout(() => setSaved(false), 2000);
      } catch (err: unknown) {
        // Pin the intended value so retry can re-fire it exactly.
        setPendingOptOut(intendedOptOut);
        setError(unflattenIpcError(err));
      } finally {
        setSaving(false);
      }
    },
    [refetch, queryClient],
  );

  const handleToggle = useCallback(
    async (nextSendAnalytics: boolean) => {
      if (saving) return;
      await performToggle(!nextSendAnalytics);
    },
    [saving, performToggle],
  );

  const handleRetry = useCallback(() => {
    if (pendingOptOut === null) return;
    void performToggle(pendingOptOut);
  }, [pendingOptOut, performToggle]);

  return (
    <div className={newUi ? 'space-y-8' : 'space-y-6'} data-testid="privacy-panel">
      <h2
        className={newUi ? 'f-title' : 'text-lg font-medium'}
        style={newUi ? { color: 'var(--text-1)' } : undefined}
      >
        Privacy
      </h2>

      <div
        className={`${newUi ? 'rounded-xl p-6' : 'rounded-lg border border-white/10 p-4'}`}
        style={newUi ? { boxShadow: 'var(--hairline)', background: 'var(--bg-elev-2)' } : undefined}
      >
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={sendAnalytics}
            disabled={saving}
            onChange={(e) => void handleToggle(e.target.checked)}
            data-testid="analytics-opt-out-toggle"
            className="mt-0.5 h-4 w-4 rounded border-white/20 bg-white/5 text-crucible-accent focus:ring-crucible-accent"
          />
          <span className="flex flex-col gap-0.5">
            <span
              className={newUi ? 'f-body' : 'text-sm text-white/80'}
              style={newUi ? { color: 'var(--text-1)' } : undefined}
            >
              Send anonymous analytics to help improve Foundry
            </span>
            <span
              className={newUi ? 'f-body' : 'text-xs text-white/40'}
              style={newUi ? { color: 'var(--text-3)' } : undefined}
            >
              Foundry sends anonymous build, app, and agent telemetry to the Weekend team so we can
              spot slow builds and fix bugs. No source code, prompts, or personal content is ever
              sent. Uncheck to opt out. Changes take effect the next time you start Foundry.
            </span>
          </span>
        </label>
      </div>

      {saving && (
        <p className="text-xs text-white/40" role="status" aria-live="polite">
          Saving…
        </p>
      )}
      {saved && !saving && (
        <p className="text-xs text-emerald-300" role="status" aria-live="polite">
          ✓ Saved. Takes effect on next launch.
        </p>
      )}
      {error && <CrucibleErrorState error={error} onRetry={handleRetry} compact />}
    </div>
  );
}

function AboutSection(): React.JSX.Element {
  const { newUi } = useFeatureFlags();
  // `available`: update detected, download started in background but not
  // necessarily complete. `downloaded`: payload is on disk and
  // `quitAndInstall` will actually install. The Settings install button
  // distinguishes these so it isn't lying to the user about
  // ready-to-install when the download is still in flight (issue #185).
  const [updateState, setUpdateState] = useState<
    'idle' | 'checking' | 'available' | 'downloaded' | 'latest' | 'error'
  >('idle');
  const [updateVersion, setUpdateVersion] = useState('');
  const [installError, setInstallError] = useState<string | null>(null);
  const { data: appVersion } = useIpcQuery('app:get-version');

  // Subscribe to the same `app:update-downloaded` push the
  // top-of-window UpdateBanner uses, plus the new `app:update-error`
  // channel that fires when the auto-updater raises an error. The
  // Settings → About surface is a second view onto the same updater
  // state, and pre-fix it was lying about ready-to-install (button
  // shown the moment availability was detected, regardless of
  // download progress) AND silently stranding users on
  // "Downloading…" if the download failed (no error transition).
  useEffect(() => {
    const unsubscribeDownloaded = window.crucible.on('app:update-downloaded', (payload) => {
      setUpdateVersion(payload.version);
      setUpdateState('downloaded');
    });
    const unsubscribeError = window.crucible.on('app:update-error', (payload) => {
      // Issue #185: a download failure must transition out of
      // the "Downloading…" trap. `'error'` shows the inline error
      // banner with the raw message. The user can click "Check
      // for updates" again to retry from a fresh state.
      setInstallError(payload.message);
      setUpdateState('error');
    });
    // Catch-up: if a download already completed before this section
    // mounted, surface it from the persisted cache.
    window.crucible
      .invoke('app:get-pending-update')
      .then((pending) => {
        if (pending) {
          setUpdateVersion(pending.version);
          setUpdateState((prev) => (prev === 'error' ? prev : 'downloaded'));
        }
      })
      .catch(() => {
        /* best-effort */
      });
    return () => {
      unsubscribeDownloaded();
      unsubscribeError();
    };
  }, []);

  const handleCheckUpdates = async () => {
    setUpdateState('checking');
    setInstallError(null);
    try {
      const result = await window.crucible.invoke('app:check-updates');
      if (result.available) {
        setUpdateVersion(result.version ?? '');
        // Only flip to 'available' if we haven't already heard
        // 'downloaded' from the push channel (race: a check that
        // takes longer than the download to complete).
        setUpdateState((prev) => (prev === 'downloaded' ? prev : 'available'));
      } else {
        setUpdateState('latest');
      }
    } catch {
      setUpdateState('error');
    }
  };

  const handleInstall = async () => {
    setInstallError(null);
    try {
      await window.crucible.invoke('app:install-update');
      // `quitAndInstall` doesn't return — Electron quits the app
      // before this resolves. If we're still here a moment later
      // the install genuinely failed.
    } catch (err) {
      setInstallError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div
      className={`pt-6 ${newUi ? '' : 'border-t border-white/10'}`}
      style={
        newUi ? { borderTop: '1px solid var(--border-alpha-1)', paddingTop: '1.5rem' } : undefined
      }
    >
      <h3 className="mb-2 font-medium" style={newUi ? { color: 'var(--text-1)' } : undefined}>
        About
      </h3>
      <p
        className={newUi ? 'f-body' : 'text-sm text-white/60'}
        style={newUi ? { color: 'var(--text-2)' } : undefined}
      >
        Foundry by Weekend
      </p>
      <p
        className={newUi ? 'f-body' : 'text-sm text-white/40'}
        style={newUi ? { color: 'var(--text-3)' } : undefined}
      >
        v{appVersion ?? '…'}
      </p>
      <p
        className={newUi ? 'mt-2 f-body' : 'mt-2 text-sm text-white/40'}
        style={newUi ? { color: 'var(--text-3)' } : undefined}
      >
        Prefer the terminal? Install the CLI with{' '}
        <code className="rounded bg-white/10 px-1 text-white/70">
          npm install -g @volley-inc/crucible
        </code>{' '}
        (requires Node.js 22+ and a{' '}
        <code className="rounded bg-white/10 px-1 text-white/70">GITHUB_TOKEN</code> with{' '}
        <code className="rounded bg-white/10 px-1 text-white/70">read:packages</code> scope). Gives
        you both <code className="rounded bg-white/10 px-1 text-white/70">crucible</code> and{' '}
        <code className="rounded bg-white/10 px-1 text-white/70">foundry</code> commands.
      </p>
      <div className="mt-2 flex items-center gap-3">
        {updateState === 'idle' && (
          <button
            onClick={handleCheckUpdates}
            className="text-sm text-crucible-accent hover:underline"
          >
            Check for updates
          </button>
        )}
        {updateState === 'checking' && <span className="text-sm text-white/40">Checking...</span>}
        {updateState === 'latest' && (
          <span className="text-sm text-green-400">You're on the latest version</span>
        )}
        {updateState === 'error' && (
          <span className="text-sm text-red-400">Could not check for updates</span>
        )}
        {updateState === 'available' && (
          <>
            <span className="text-sm text-yellow-400">v{updateVersion} available</span>
            <button
              type="button"
              disabled
              title="Downloading the update — install becomes available once it finishes."
              className="rounded-lg bg-white/10 px-3 py-1 text-sm font-medium text-white/60"
            >
              Downloading…
            </button>
          </>
        )}
        {updateState === 'downloaded' && (
          <>
            <span className="text-sm text-yellow-400">v{updateVersion} downloaded</span>
            <button
              type="button"
              onClick={() => void handleInstall()}
              className="rounded-lg bg-crucible-accent px-3 py-1 text-sm font-medium text-white hover:opacity-90"
            >
              Install and restart
            </button>
          </>
        )}
      </div>
      {installError && (
        <p className="mt-2 text-sm text-red-400" data-testid="install-error">
          Install failed: {installError}
        </p>
      )}
    </div>
  );
}

function TokenGuide({ type }: { type: 'github' | 'anthropic' | 'fal' }): React.JSX.Element {
  const { newUi } = useFeatureFlags();
  const [expanded, setExpanded] = useState(false);

  if (type === 'github') {
    return (
      <div className="space-y-2">
        <p
          className={`text-sm ${newUi ? '' : 'text-white/50'}`}
          style={newUi ? { color: 'var(--text-2)' } : undefined}
        >
          You need a GitHub personal access token to create and manage game repositories.
        </p>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-sm text-crucible-accent hover:underline"
        >
          {expanded ? 'Hide instructions ▲' : 'How do I get this? ▼'}
        </button>
        {expanded && (
          <div
            className={`p-4 text-sm space-y-3 ${newUi ? 'rounded-xl' : 'rounded-lg bg-white/5 text-white/60'}`}
            style={
              newUi
                ? {
                    boxShadow: 'var(--hairline)',
                    background: 'var(--bg-elev-1)',
                    color: 'var(--text-2)',
                  }
                : undefined
            }
          >
            <p>
              <strong className="text-white/80">Step 1:</strong> Go to{' '}
              <button
                onClick={() => window.open('https://github.com/settings/tokens', '_blank')}
                className="text-crucible-accent hover:underline"
              >
                github.com/settings/tokens
              </button>
            </p>
            <p>
              <strong className="text-white/80">Step 2:</strong> Click{' '}
              <strong className="text-white/80">"Generate new token"</strong> and select{' '}
              <strong className="text-white/80">"Generate new token (classic)"</strong> — not the
              fine-grained option.
            </p>
            <p>
              <strong className="text-white/80">Step 3:</strong> Give it a name like{' '}
              <code className="rounded bg-white/10 px-1">Foundry</code>
            </p>
            <p>
              <strong className="text-white/80">Step 4:</strong> Select these scopes:
            </p>
            <ul className="ml-4 list-disc space-y-1">
              <li>
                <code className="rounded bg-white/10 px-1">repo</code> — Full control of
                repositories
              </li>
              <li>
                <code className="rounded bg-white/10 px-1">workflow</code> — Update GitHub Actions
                workflows
              </li>
            </ul>
            <p>
              <strong className="text-white/80">Step 5:</strong> Click{' '}
              <strong className="text-white/80">"Generate token"</strong> and copy the value. It
              starts with <code className="rounded bg-white/10 px-1">ghp_</code>
            </p>
            <p
              className={`text-xs ${newUi ? '' : 'text-white/40'}`}
              style={newUi ? { color: 'var(--text-3)' } : undefined}
            >
              Your token is stored securely in your operating system's keychain — it never leaves
              your machine.
            </p>
          </div>
        )}
      </div>
    );
  }

  if (type === 'fal') {
    return (
      <div className="space-y-2">
        <p
          className={`text-sm ${newUi ? '' : 'text-white/50'}`}
          style={newUi ? { color: 'var(--text-2)' } : undefined}
        >
          You need a Fal.ai API key to generate AI artwork for game tiles.
        </p>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-sm text-crucible-accent hover:underline"
        >
          {expanded ? 'Hide instructions ▲' : 'How do I get this? ▼'}
        </button>
        {expanded && (
          <div
            className={`p-4 text-sm space-y-3 ${newUi ? 'rounded-xl' : 'rounded-lg bg-white/5 text-white/60'}`}
            style={
              newUi
                ? {
                    boxShadow: 'var(--hairline)',
                    background: 'var(--bg-elev-1)',
                    color: 'var(--text-2)',
                  }
                : undefined
            }
          >
            <p>
              <strong className="text-white/80">Step 1:</strong> Go to{' '}
              <button
                onClick={() => window.open('https://fal.ai/dashboard/keys', '_blank')}
                className="text-crucible-accent hover:underline"
              >
                fal.ai/dashboard/keys
              </button>
            </p>
            <p>
              <strong className="text-white/80">Step 2:</strong> Click{' '}
              <strong className="text-white/80">"Create Key"</strong>
            </p>
            <p>
              <strong className="text-white/80">Step 3:</strong> Copy the key and paste it below.
            </p>
            <p
              className={`text-xs ${newUi ? '' : 'text-white/40'}`}
              style={newUi ? { color: 'var(--text-3)' } : undefined}
            >
              Your key is stored securely in your operating system's keychain — it never leaves your
              machine.
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p
        className={`text-sm ${newUi ? '' : 'text-white/50'}`}
        style={newUi ? { color: 'var(--text-2)' } : undefined}
      >
        You need an Anthropic API key to use AI game building features.
      </p>
      <button
        onClick={() => setExpanded(!expanded)}
        className="text-sm text-crucible-accent hover:underline"
      >
        {expanded ? 'Hide instructions ▲' : 'How do I get this? ▼'}
      </button>
      {expanded && (
        <div
          className={`p-4 text-sm space-y-3 ${newUi ? 'rounded-xl' : 'rounded-lg bg-white/5 text-white/60'}`}
          style={
            newUi
              ? {
                  boxShadow: 'var(--hairline)',
                  background: 'var(--bg-elev-1)',
                  color: 'var(--text-2)',
                }
              : undefined
          }
        >
          <p>
            <strong className="text-white/80">Option A — From your team lead:</strong> Ask your team
            lead or manager for the shared Anthropic API key.
          </p>
          <p>
            <strong className="text-white/80">Option B — From the Anthropic Console:</strong>
          </p>
          <ol className="ml-4 list-decimal space-y-1">
            <li>
              Go to{' '}
              <button
                onClick={() => window.open('https://console.anthropic.com/settings/keys', '_blank')}
                className="text-crucible-accent hover:underline"
              >
                console.anthropic.com/settings/keys
              </button>
            </li>
            <li>
              Click <strong className="text-white/80">"Create Key"</strong>
            </li>
            <li>
              Copy the key — it starts with{' '}
              <code className="rounded bg-white/10 px-1">sk-ant-</code>
            </li>
          </ol>
          <p
            className={`text-xs ${newUi ? '' : 'text-white/40'}`}
            style={newUi ? { color: 'var(--text-3)' } : undefined}
          >
            Your key is stored securely in your operating system's keychain — it never leaves your
            machine.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Settings → Diagnostics. Two sections today:
 *
 * 1. **System tools** — ✓/✗ + version for git, Node, pnpm. Driven by
 *    `tokens:verify`'s system-tool fields (added with the prereq-checks
 *    work for CRUCIBLE-PREREQ-MISSING). Mode `'full'` so the surface
 *    is informative even when everything's installed.
 *
 * 2. **Logs** — copies the bundled `collectDiagnostics` blob to the
 *    clipboard. Already token-scrubbed by `redactSecrets` in main, so
 *    pasting into Slack is safe. The blob includes Foundry version,
 *    OS, Electron/Node/Chrome versions, the resolved logs dir, and
 *    the last 200 log lines.
 */
/** Cap on how long the rich-diagnostics probe is allowed to run before
 * we give up and render the panel WITHOUT the expand-row data (Codex
 * Round-1 BUG 5). 10s is generous — a healthy `which/where + --version`
 * round-trip is sub-second; anything past 10s is a hung pnpm. */
export const DIAGNOSTICS_PROBE_TIMEOUT_MS = 10_000;

/**
 * Race a probe promise against a timeout. Resolves to either the
 * probe's value (if it wins) or the sentinel `'timeout'` (if the
 * timer fires first or the probe rejects). Exported so the renderer
 * test can pin the four branches without spinning up a full Settings
 * panel.
 *
 * Codex Round-1 BUG 5: this used to be inlined in `DiagnosticsPanel`
 * with a `'__timeout__'` string sentinel and `try/catch` around
 * `Promise.race`. Extracting here:
 *   - lets tests exercise the race semantics deterministically
 *     (resolved-fast / resolved-slow / rejected-pre-timeout /
 *     rejected-post-timeout branches),
 *   - cleans up the timer on every exit path (no stranded
 *     `setTimeout` if the probe rejects mid-race),
 *   - centralises the contract so future callers (a future "Refresh
 *     diagnostics only" button, say) reuse the same behaviour.
 *
 * Codex Round-2 Fix 2: a probe that REJECTED after the timeout had
 * already won used to escape as an unhandled rejection (the original
 * `probe.then(...)` chain had no `.catch` once the race settled). The
 * implementation now wraps the probe with both resolution AND
 * rejection handlers gated on an `aborted` flag the timeout sets, so
 * late-rejecting probes settle cleanly into `{ kind: 'timeout' }`
 * without surfacing on `process.on('unhandledRejection', ...)`.
 */
export type RaceResult<T> = { kind: 'value'; value: T } | { kind: 'timeout' };

export function raceWithTimeout<T>(probe: Promise<T>, timeoutMs: number): Promise<RaceResult<T>> {
  // Codex Round-2 Fix 2: the previous implementation passed `probe`
  // directly to `Promise.race`, which means the probe's rejection
  // handler was only attached via the race itself. Once the timeout
  // won the race, the original `probe.then(value => ...)` chain still
  // existed without a `.catch` — so a probe that rejected AFTER the
  // race had already settled surfaced as an UNHANDLED rejection
  // ("UnhandledPromiseRejectionWarning") and crashed the renderer
  // process under Electron's strict mode.
  //
  // The fix: attach BOTH resolution and rejection handlers to a wrapped
  // copy of the probe, with an `aborted` flag the timeout sets to
  // true. Late-arriving values resolve to `{ kind: 'timeout' }` (the
  // race result is ignored anyway, but the handler runs cleanly).
  // Late-arriving rejections ALSO collapse to `{ kind: 'timeout' }`
  // when aborted — the rejection is "swallowed" but ONLY when we've
  // already abandoned the result, which is the exact case the
  // unhandled-rejection warning was complaining about. Pre-timeout
  // rejections still propagate via `Promise.reject(err)` so the
  // surrounding `Promise.race` rejects and the outer `.catch` (we
  // attach one below for symmetry) collapses to a timeout result —
  // same UI fallback either way.
  let aborted = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<RaceResult<T>>((resolve) => {
    timer = setTimeout(() => {
      aborted = true;
      resolve({ kind: 'timeout' });
    }, timeoutMs);
  });
  const wrapped: Promise<RaceResult<T>> = probe.then(
    (value): RaceResult<T> => (aborted ? { kind: 'timeout' } : { kind: 'value', value }),
    (err): RaceResult<T> | Promise<RaceResult<T>> => {
      // Pre-timeout rejection: re-throw so the outer race rejects and
      // the trailing `.catch` collapses to a timeout result.
      // Post-timeout rejection: swallow into a timeout result so the
      // dangling promise settles cleanly without an unhandled-
      // rejection warning.
      return aborted ? { kind: 'timeout' } : Promise.reject(err);
    },
  );
  return Promise.race([wrapped, timeoutPromise])
    .catch((): RaceResult<T> => ({ kind: 'timeout' }))
    .finally(() => {
      if (timer !== undefined) clearTimeout(timer);
    });
}

function DiagnosticsPanel(): React.JSX.Element {
  const { newUi } = useFeatureFlags();
  const [verify, setVerify] = useState<TokenVerifyResult | null>(null);
  const [verifyError, setVerifyError] = useState<SerialisedError | null>(null);
  const [diagnostics, setDiagnostics] = useState<
    Partial<
      Record<
        'git' | 'node' | 'pnpm',
        import('@volley/crucible-ipc-types/system-prereqs').SystemToolDiagnostic
      >
    >
  >({});
  const [diagnosticsUnavailable, setDiagnosticsUnavailable] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState<SerialisedError | null>(null);
  // Cloud-VM supportability TDD §C-1 — Copy gate state.
  //
  //   - `bundleText` is the rendered diagnostics blob (the same string
  //     `app:get-diagnostics` returns today). Fetched on Preview-click
  //     OR lazily on first Copy attempt.
  //   - `secretFlags` is the output of `scanForSecrets(bundleText)`.
  //     `[]` enables Copy directly; non-empty requires `reviewedFlags`.
  //   - `reviewedFlags` is the user's tick of the "I've reviewed all
  //     flagged lines" checkbox. Resets to false on every re-fetch
  //     (a refresh re-scans, and a stale ack must not auto-apply).
  //   - `previewVisible` toggles the inline preview. Off by default
  //     so the section stays compact; opening it triggers the fetch
  //     + scan, which also primes the Copy gate.
  const [bundleText, setBundleText] = useState<string | null>(null);
  const [bundleFetching, setBundleFetching] = useState(false);
  const [secretFlags, setSecretFlags] = useState<SecretFlag[]>([]);
  const [reviewedFlags, setReviewedFlags] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);

  const refresh = useCallback(async () => {
    // Codex Round-1 BUG 5: token verification and the rich-diagnostics
    // probe run on INDEPENDENT lifecycles so a slow pnpm doesn't wedge
    // the badge surface. The `refreshing` flag is tied to the token
    // call only — that's the operation the user thinks they're
    // waiting on when they hit "Re-check". The diagnostics probe is
    // backgrounded and bounded with a 10s timeout; if it loses the
    // race we render the panel without the expand-row data and
    // surface a small "diagnostics unavailable" amber note.
    setRefreshing(true);
    setVerifyError(null);
    setDiagnosticsUnavailable(false);

    // Token verification — drives the `refreshing` state and the
    // green/red badges. Resolved inline so the spinner clears as
    // soon as the user-relevant call returns, regardless of what
    // the diagnostics probe is doing.
    try {
      const result = await window.crucible.invoke('tokens:verify');
      setVerify(result);
    } catch (err) {
      setVerifyError(unflattenIpcError(err));
    } finally {
      setRefreshing(false);
    }

    // Diagnostics probe — race the IPC call against a timeout so a
    // hung pnpm can't wedge the panel forever. We run this AFTER
    // token verify resolves so failures don't compete for screen
    // real estate, but the probe itself doesn't depend on the
    // token result so the order is purely a UX nicety. `raceWithTimeout`
    // collapses both "timed out" and "rejected" into `kind: 'timeout'`
    // — the UI fallback is the same in both cases.
    const probed = await raceWithTimeout(
      window.crucible.invoke('diagnostics:probe-system-tools'),
      DIAGNOSTICS_PROBE_TIMEOUT_MS,
    );
    if (probed.kind === 'value') {
      setDiagnostics(probed.value);
    } else {
      setDiagnosticsUnavailable(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Fetch the diagnostics blob + run the secret-scan tripwire. The
  // returned string + flag list also prime the inline preview.
  // Resets `reviewedFlags` to false — a fresh fetch invalidates any
  // prior ack so the user has to look again. Returns the fetched
  // text so the caller can avoid a state-update round-trip when
  // immediately copying.
  const fetchBundleAndScan = useCallback(async (): Promise<{
    text: string;
    flags: SecretFlag[];
  } | null> => {
    setBundleFetching(true);
    setCopyError(null);
    try {
      const text = await window.crucible.invoke('app:get-diagnostics');
      const flags = scanForSecrets(text);
      setBundleText(text);
      setSecretFlags(flags);
      setReviewedFlags(false);
      return { text, flags };
    } catch (err) {
      setCopyError(unflattenIpcError(err));
      return null;
    } finally {
      setBundleFetching(false);
    }
  }, []);

  const copyDiagnostics = useCallback(async () => {
    setCopyError(null);
    // If the bundle hasn't been fetched yet, fetch + scan first.
    // If flags exist AND the user hasn't ticked the review checkbox,
    // refuse to write to the clipboard. The Copy button is also
    // disabled in that state in the UI, but defence-in-depth: the
    // callback itself enforces the gate so a stale render or a
    // keyboard shortcut can't bypass it.
    let text = bundleText;
    let flags = secretFlags;
    if (text === null) {
      const fetched = await fetchBundleAndScan();
      if (!fetched) return;
      text = fetched.text;
      flags = fetched.flags;
      // Force the preview visible so the user can actually see what
      // they're about to copy when there's something to review.
      if (flags.length > 0) setPreviewVisible(true);
    }
    if (flags.length > 0 && !reviewedFlags) {
      // Belt-and-braces: button is disabled in this state too.
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setCopyError(unflattenIpcError(err));
    }
  }, [bundleText, secretFlags, reviewedFlags, fetchBundleAndScan]);

  const togglePreview = useCallback(async () => {
    if (!previewVisible && bundleText === null) {
      await fetchBundleAndScan();
    }
    setPreviewVisible((v) => !v);
  }, [previewVisible, bundleText, fetchBundleAndScan]);

  const copyDisabled = bundleFetching || (secretFlags.length > 0 && !reviewedFlags);

  return (
    <div className="max-w-2xl space-y-8">
      <div>
        <h2
          className={newUi ? 'f-title' : 'text-lg font-medium'}
          style={newUi ? { color: 'var(--text-1)' } : undefined}
        >
          System tools
        </h2>
        <p
          className={`mt-1 text-sm ${newUi ? '' : 'text-white/50'}`}
          style={newUi ? { color: 'var(--text-2)' } : undefined}
        >
          Foundry's spawned subprocesses (game scaffolding, dev server, publish) need these on PATH.
          Missing tools block <strong>+ New Game</strong> with a clear error code instead of failing
          mid-flow.
        </p>
        {/*
          Issue #310: explain the Corepack-first hybrid to users so they
          stop trying to debug their own pnpm install. Foundry now resolves
          the pnpm version pinned in the game's `package.json` via Corepack,
          which ships with Node 22+. If Corepack itself is unavailable,
          Foundry falls back to a bundled pnpm 10.27.0 inside the app.
          Practical upshot: a working `node` install is the only pnpm
          prerequisite — your Homebrew/Volta/asdf-managed pnpm is no
          longer load-bearing for Foundry workflows.
        */}
        <p
          className={`mt-2 text-xs ${newUi ? '' : 'text-white/40'}`}
          style={newUi ? { color: 'var(--text-3)' } : undefined}
        >
          Foundry uses Corepack to run the pnpm version pinned in your game's{' '}
          <code className="rounded bg-white/5 px-1">package.json</code>. If Corepack is unavailable,
          Foundry falls back to a bundled pnpm 10.27.0. You no longer need to manage your own pnpm
          install.
        </p>
        {verify && (
          <div className="mt-4">
            <PrereqStatusList verify={verify} mode="full" diagnostics={diagnostics} />
          </div>
        )}
        {diagnosticsUnavailable && (
          // Codex Round-1 BUG 5: rich-diagnostics probe timed out
          // (or rejected). The badge surface above is still valid;
          // we just couldn't get the resolved-path / shebang detail
          // for the expand-row in time. Keep the note small and
          // amber so users know there's nothing wrong with the tool
          // status — only the deep-detail view degraded.
          <div
            data-testid="diagnostics-unavailable-note"
            className="mt-3 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-300/80"
          >
            Diagnostic details unavailable (probe timed out). Tool status above is still accurate —
            re-check to retry the deep probe.
          </div>
        )}
        {verifyError && (
          <div className="mt-4">
            <CrucibleErrorState error={verifyError} compact onRetry={() => void refresh()} />
          </div>
        )}
        <button
          onClick={() => void refresh()}
          disabled={refreshing}
          className={`mt-3 rounded-lg px-3 py-1.5 text-xs disabled:opacity-40 ${newUi ? '' : 'border border-white/10 text-white/70 hover:bg-white/5'}`}
          style={
            newUi
              ? {
                  boxShadow: 'var(--hairline)',
                  color: 'var(--text-2)',
                  background: 'var(--bg-elev-3)',
                }
              : undefined
          }
        >
          {refreshing ? 'Re-checking…' : 'Re-check'}
        </button>
      </div>

      <div>
        <h2
          className={newUi ? 'f-title' : 'text-lg font-medium'}
          style={newUi ? { color: 'var(--text-1)' } : undefined}
        >
          Logs
        </h2>
        <p
          className={`mt-1 text-sm ${newUi ? '' : 'text-white/50'}`}
          style={newUi ? { color: 'var(--text-2)' } : undefined}
        >
          Bundle Foundry version, OS, runtime versions, and the last 200 log lines into a paste-able
          blob. API keys, GitHub PATs, and other token-shaped strings are scrubbed automatically
          before the text leaves the process — and the Copy button blocks if any token-shaped
          strings survived the scrub so you can review them before pasting.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            onClick={() => void copyDiagnostics()}
            disabled={copyDisabled}
            data-testid="copy-diagnostics-button"
            className="rounded-lg bg-crucible-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {copied ? 'Copied to clipboard' : 'Copy diagnostics'}
          </button>
          <button
            onClick={() => void togglePreview()}
            data-testid="toggle-preview-button"
            className={`rounded-lg px-3 py-1.5 text-xs ${newUi ? '' : 'border border-white/10 text-white/70 hover:bg-white/5'}`}
            style={
              newUi
                ? {
                    boxShadow: 'var(--hairline)',
                    color: 'var(--text-2)',
                    background: 'var(--bg-elev-3)',
                  }
                : undefined
            }
          >
            {previewVisible ? 'Hide preview' : 'Show preview'}
          </button>
          <span
            className={`text-xs ${newUi ? '' : 'text-white/30'}`}
            style={newUi ? { color: 'var(--text-3)' } : undefined}
          >
            Paste into a Slack thread for the Foundry team to dig in.
          </span>
        </div>
        {previewVisible && (
          <div className="mt-3 space-y-2">
            {secretFlags.length > 0 && <SecretFlagBanner flags={secretFlags} />}
            {bundleText !== null && <BundlePreview text={bundleText} flags={secretFlags} />}
            {secretFlags.length > 0 && (
              <label
                className="flex cursor-pointer items-start gap-2 text-xs text-white/70"
                data-testid="reviewed-flags-checkbox"
              >
                <input
                  type="checkbox"
                  checked={reviewedFlags}
                  onChange={(e) => setReviewedFlags(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  I&rsquo;ve reviewed all flagged lines and confirm none are secrets.{' '}
                  <span className="text-white/40">
                    (Per-line acknowledgement is coming in a future release.)
                  </span>
                </span>
              </label>
            )}
          </div>
        )}
        {copyError && (
          <div className="mt-3">
            <CrucibleErrorState error={copyError} compact onRetry={() => void copyDiagnostics()} />
          </div>
        )}
      </div>
    </div>
  );
}
